import { isShuttleVehicle } from "features/CenterContent/VehicleDetails/AlternativeTransportDetails/utils";
import { VehicleCancellation } from "features/CenterContent/VehicleDetails/AlternativeTransportDetails/Itinerary/VehicleCancellation/VehicleCancellation";
import { VehicleUndoCancellation } from "features/CenterContent/VehicleDetails/AlternativeTransportDetails/Itinerary/VehicleCancellation/VehicleUndoCancellation";
import { VehicleSummary } from "features/CenterContent/VehicleDetails/shared/VehicleSummary";
import { vehicleTypeToIcon } from "features/CenterContent/VehicleDetails/shared/utils";
import { removeStationFromStopName } from "features/VehicleSidebar/VehicleList/utils/stopNameUtils";
import { FC } from "react";
import { HStack, Text, VStack } from "shared/components/Spor";
import { AlternativeTransportVehicle } from "shared/types/alternativeTransport";
import styled, { useTheme } from "styled-components";

type Props = {
  alternativeTransport: AlternativeTransportVehicle;
};

const BodyActions = styled.div`
  display: none;

  @container altTransportPage (width >= 1696px) {
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    gap: 4px;
    margin-top: 8px;
  }
`;

const OperatorText = styled.div`
  position: absolute;
  right: 0;
  bottom: 0;

  @container altTransportPage (width >= 1696px) {
    position: static;
    margin-top: auto;
  }
`;

export const Itinerary: FC<Props> = ({ alternativeTransport }) => {
  const {
    lineNames,
    tripPatternId,
    trainIds,
    itinerary,
    type,
    vehicleStatus,
    driverPhoneNumber,
    hastusShuttlePlanId,
    originStopName,
    destinationStopName,
    firstStopName,
    scheduledDepartureTimeOrigin,
    scheduledArrivalTimeDestination,
    logs,
    operatorName,
  } = alternativeTransport;

  // Prefix unused variables with an underscore so the linter accepts them as intentionally unused
  const [_Icon, _iconTitle] = vehicleTypeToIcon.get(type)!;

  const isShuttle = isShuttleVehicle(alternativeTransport);
  const returnStops = itinerary?.returnStops ?? [];
  const theme = useTheme();

  const turnaroundStopName =
    returnStops.length > 0 ? returnStops[returnStops.length - 1].name : null;

  const replacesTrainIds = trainIds.length > 0 ? trainIds.join(", ") : null;

  const tripDetails = [lineNames, hastusShuttlePlanId || tripPatternId]
    .filter((stop): stop is string => stop !== null && stop !== "")
    .join(" / ");

  const stations = [
    removeStationFromStopName(originStopName),
    removeStationFromStopName(destinationStopName),
    turnaroundStopName,
  ]
    .filter((stop): stop is string => stop !== null && stop !== "")
    .join("–");

  return (
    <HStack alignItems="stretch" mt="-8px" w="100%" position="relative">
      <VehicleSummary
        title={replacesTrainIds ?? _iconTitle}
        type={type}
        subTitle={tripDetails}
        route={stations.concat(
          isShuttle && firstStopName && firstStopName !== originStopName
            ? ` (oppstart ${firstStopName})`
            : "",
        )}
        startTime={scheduledDepartureTimeOrigin}
        endTime={scheduledArrivalTimeDestination}
        vehicleStatus={vehicleStatus}
        logs={logs}
        driverPhoneNumber={driverPhoneNumber}
      />
      <VStack align="end" minW="0" gap={1}>
        <BodyActions>
          <VehicleCancellation vehicle={alternativeTransport} />
          <VehicleUndoCancellation vehicle={alternativeTransport} />
        </BodyActions>
        <OperatorText>
          <Text
            fontStyle="italic"
            variant="sm"
            whiteSpace="nowrap"
            color={theme.colorTextSecondary}
          >
            Kjøres av: {operatorName}
          </Text>
        </OperatorText>
      </VStack>
    </HStack>
  );
};
